import { useEffect, useState, useMemo, useRef } from "react";
import { toast } from "sonner";
import {
  Users, Trash2, ShieldAlert, ShieldCheck, Pencil, FileCog, Search, X, Mail, Plus, ListFilter,
} from "lucide-react";
import api from "@/lib/api";
import { bucketBadgeClass } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuCheckboxItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const BASIS_OPTIONS = ["Days from BL", "Days From Invoice", "On Delivery", "Against BL"];
const BASIS = {
  domestic: BASIS_OPTIONS,
  export: BASIS_OPTIONS,
};

// Chips input for a list of emails
function EmailsInput({ value, onChange, testid, placeholder }) {
  const [draft, setDraft] = useState("");
  const add = () => {
    const parts = draft.split(/[,;\s]+/).map((s) => s.trim()).filter(Boolean);
    if (parts.length) onChange([...new Set([...(value || []), ...parts])]);
    setDraft("");
  };
  return (
    <div className="rounded-md border border-input p-2 space-y-2" data-testid={testid}>
      <div className="flex flex-wrap gap-1.5">
        {(value || []).map((e) => (
          <span key={e} className="inline-flex items-center gap-1 bg-primary/10 text-primary text-xs rounded-full px-2 py-0.5">
            {e}
            <button type="button" onClick={() => onChange(value.filter((x) => x !== e))} className="hover:text-rose-500">
              <X className="w-3 h-3" />
            </button>
          </span>
        ))}
        {(!value || value.length === 0) && <span className="text-xs text-muted-foreground px-1">No emails yet</span>}
      </div>
      <div className="flex gap-2">
        <Input value={draft} onChange={(e) => setDraft(e.target.value)}
               onKeyDown={(e) => { if (e.key === "Enter" || e.key === ",") { e.preventDefault(); add(); } }}
               placeholder={placeholder || "type email and press Enter"} className="h-8 text-sm" />
        <Button type="button" variant="outline" size="sm" onClick={add} className="h-8"><Plus className="w-4 h-4" /></Button>
      </div>
    </div>
  );
}

function CcSettings() {
  const [open, setOpen] = useState(false);
  const [ccDom, setCcDom] = useState([]);
  const [ccExp, setCcExp] = useState([]);
  const load = () => api.get("/settings/email").then(({ data }) => { setCcDom(data.cc_domestic || []); setCcExp(data.cc_export || []); });
  useEffect(() => { load(); }, []);
  const save = async () => {
    try {
      await api.put("/settings/email", { cc_domestic: ccDom, cc_export: ccExp });
      toast.success("CC recipients saved");
      setOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save (admin only)");
    }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" data-testid="cc-settings-button"><Mail className="w-4 h-4 mr-2" /> CC Recipients</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Mail className="w-5 h-5 text-primary" /> Reminder CC Recipients</DialogTitle>
          <DialogDescription>These addresses are CC'd on every reminder — separately for domestic and export customers.</DialogDescription>
        </DialogHeader>
        <div className="space-y-1.5">
          <Label>CC for Domestic customers</Label>
          <EmailsInput value={ccDom} onChange={setCcDom} testid="cc-domestic-input" />
        </div>
        <div className="space-y-1.5">
          <Label>CC for Export customers</Label>
          <EmailsInput value={ccExp} onChange={setCcExp} testid="cc-export-input" />
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={save} data-testid="cc-save-button">Save CC list</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Customers() {
  const [rows, setRows] = useState(null);
  const [excludeTarget, setExcludeTarget] = useState(null);
  const [reason, setReason] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [masterTarget, setMasterTarget] = useState(null);
  const [master, setMaster] = useState({});
  const [masterInit, setMasterInit] = useState({});
  const closingMaster = useRef(false);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [emailFilter, setEmailFilter] = useState("all");
  const [refFilter, setRefFilter] = useState([]);

  const load = async () => {
    const { data } = await api.get("/customers");
    setRows(data);
  };
  useEffect(() => { load(); }, []);

  const doToggle = async (c, next, reasonText = "") => {
    try {
      await api.patch(`/customers/${encodeURIComponent(c.id)}/exclude`, { excluded: next, reason: reasonText });
      toast.success(next ? `${c.customer_name} excluded from reminders` : `${c.customer_name} re-included`);
      load();
    } catch { toast.error("Could not update customer"); }
  };
  const onSwitch = (c, next) => { if (next) { setExcludeTarget(c); setReason(""); } else doToggle(c, false); };
  const confirmExclude = async () => { await doToggle(excludeTarget, true, reason); setExcludeTarget(null); };

  const confirmDelete = async () => {
    try {
      await api.delete(`/customers/${encodeURIComponent(deleteTarget.id)}`);
      toast.success(`${deleteTarget.customer_name} removed`);
      setDeleteTarget(null); load();
    } catch { toast.error("Could not remove customer"); }
  };

  const openMaster = (c) => {
    setMasterTarget(c);
    const init = {
      payment_terms_days: c.payment_terms_days ?? 30,
      payment_terms_basis: c.payment_terms_basis || (c.source === "export" ? "Days from Invoice" : "Days from Invoice Date"),
      credit_limit: c.credit_limit ?? 0,
      contact_person: c.contact_person ?? "",
      emails: c.emails || (c.customer_email ? [c.customer_email] : []),
    };
    setMaster(init);
    setMasterInit(init);
  };

  const saveMaster = async () => {
    await api.patch(`/customers/${encodeURIComponent(masterTarget.id)}/master`, master);
    toast.success(`${masterTarget.customer_name} saved`);
    load();
  };

  // Auto-save on close (only when something actually changed)
  const closeMaster = async () => {
    if (closingMaster.current) return;
    closingMaster.current = true;
    const changed = masterTarget && JSON.stringify(master) !== JSON.stringify(masterInit);
    try {
      if (changed) await saveMaster();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save customer master");
    } finally {
      setMasterTarget(null);
      closingMaster.current = false;
    }
  };

  const refOptions = useMemo(
    () => Array.from(new Set((rows || []).map((c) => c.payment_terms_basis).filter(Boolean))).sort(),
    [rows]
  );
  const toggleRef = (ref) =>
    setRefFilter((prev) => (prev.includes(ref) ? prev.filter((r) => r !== ref) : [...prev, ref]));

  const filtered = useMemo(() => {
    let out = rows || [];
    if (typeFilter !== "all") out = out.filter((c) => c.source === typeFilter);
    if (statusFilter === "excluded") out = out.filter((c) => c.excluded);
    if (statusFilter === "active") out = out.filter((c) => !c.excluded);
    if (emailFilter === "has") out = out.filter((c) => (c.emails || []).length > 0);
    if (emailFilter === "no") out = out.filter((c) => (c.emails || []).length === 0);
    if (refFilter.length) out = out.filter((c) => refFilter.includes(c.payment_terms_basis));
    if (search) {
      const q = search.toLowerCase();
      out = out.filter((c) => c.customer_name.toLowerCase().includes(q) ||
        (c.emails || []).join(" ").toLowerCase().includes(q));
    }
    return out;
  }, [rows, typeFilter, statusFilter, emailFilter, refFilter, search]);

  const hasFilters = search || typeFilter !== "all" || statusFilter !== "all" || emailFilter !== "all" || refFilter.length > 0;
  const clearAll = () => { setSearch(""); setTypeFilter("all"); setStatusFilter("all"); setEmailFilter("all"); setRefFilter([]); };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">Customers</h1>
          <p className="text-muted-foreground mt-1 text-sm max-w-2xl">
            The customer master. Add To emails here — reminders are sent automatically to them. Set CC recipients (domestic/export) once for all reminders.
          </p>
        </div>
        <CcSettings />
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <Tabs value={typeFilter} onValueChange={setTypeFilter}>
          <TabsList>
            <TabsTrigger value="all" data-testid="cust-type-all">All</TabsTrigger>
            <TabsTrigger value="domestic" data-testid="cust-type-domestic">Domestic</TabsTrigger>
            <TabsTrigger value="export" data-testid="cust-type-export">Export</TabsTrigger>
          </TabsList>
        </Tabs>
        <div className="relative flex-1 min-w-[180px] max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)}
                 placeholder="Search name or email…" className="pl-9" data-testid="cust-search-input" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[150px]" data-testid="cust-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="active">Active only</SelectItem>
            <SelectItem value="excluded">Won't-pay only</SelectItem>
          </SelectContent>
        </Select>
        <Select value={emailFilter} onValueChange={setEmailFilter}>
          <SelectTrigger className="w-[150px]" data-testid="cust-email-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Any email</SelectItem>
            <SelectItem value="has">Has email</SelectItem>
            <SelectItem value="no">Missing email</SelectItem>
          </SelectContent>
        </Select>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" className="h-10 gap-2" data-testid="cust-ref-filter">
              <ListFilter className="w-4 h-4" />
              Document Ref
              {refFilter.length > 0 && (
                <Badge variant="secondary" className="ml-1 h-5 px-1.5 text-[10px]">{refFilter.length}</Badge>
              )}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-64">
            <DropdownMenuLabel>Filter by document reference</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {refOptions.length === 0 ? (
              <div className="px-2 py-1.5 text-xs text-muted-foreground">No references set yet</div>
            ) : (
              refOptions.map((ref) => (
                <DropdownMenuCheckboxItem
                  key={ref}
                  checked={refFilter.includes(ref)}
                  onCheckedChange={() => toggleRef(ref)}
                  onSelect={(e) => e.preventDefault()}
                  data-testid={`cust-ref-option-${ref}`}
                >
                  {ref}
                </DropdownMenuCheckboxItem>
              ))
            )}
            {refFilter.length > 0 && (
              <>
                <DropdownMenuSeparator />
                <button
                  onClick={() => setRefFilter([])}
                  className="w-full text-left px-2 py-1.5 text-xs text-muted-foreground hover:text-foreground"
                  data-testid="cust-ref-clear"
                >
                  Clear references
                </button>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
        {rows && <Badge variant="outline" className="text-xs" data-testid="cust-count">{filtered.length} of {rows.length}</Badge>}
        {hasFilters && (
          <Button variant="ghost" size="sm" onClick={clearAll} className="h-8 text-xs text-muted-foreground" data-testid="cust-clear-filters">
            <X className="w-3 h-3 mr-1" /> Clear
          </Button>
        )}
      </div>

      <Card className="overflow-hidden" data-testid="customers-table">
        {!rows ? (
          <div className="p-6 space-y-3">{[...Array(6)].map((_, i) => <Skeleton key={i} className="h-10" />)}</div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center text-sm text-muted-foreground">
            <Users className="w-8 h-8 mx-auto mb-3 text-muted-foreground" />
            No customers match your filters.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50 hover:bg-muted/50">
                  <TableHead className="text-xs uppercase tracking-wider">Customer</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider">Type</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider whitespace-nowrap">Days</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider">Document Reference</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider">Invoices</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider">Oldest</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-right">Outstanding</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-center">Won't Pay</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-right">Remove</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((c) => (
                  <TableRow key={c.id} className="hover:bg-muted/40 transition-colors" data-testid={`customer-row-${c.id}`}>
                    <TableCell className="font-medium min-w-[180px] max-w-[280px]">
                      <span className={`break-words ${c.excluded ? "line-through text-muted-foreground" : ""}`}>{c.customer_name}</span>
                      <div className="text-[11px] text-muted-foreground break-words">
                        {(c.emails || []).length ? c.emails.join(", ") : <span className="text-amber-600 dark:text-amber-500">no email</span>}
                      </div>
                      {c.excluded && c.exclude_reason && (
                        <div className="text-[11px] text-rose-500 mt-0.5">Reason: {c.exclude_reason}</div>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={c.source === "domestic"
                        ? "bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20"
                        : "bg-purple-500/10 text-purple-600 dark:text-purple-400 border-purple-500/20"}>
                        {c.source}
                      </Badge>
                    </TableCell>
                    <TableCell className="whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" data-testid={`customer-terms-${c.id}`}>Net {c.payment_terms_days ?? 30}d</Badge>
                        <Button variant="ghost" size="icon" className="h-6 w-6 text-muted-foreground hover:text-primary"
                                onClick={() => openMaster(c)} data-testid={`customer-master-edit-${c.id}`}>
                          <Pencil className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                      {c.credit_limit > 0 && (
                        <div className="text-[11px] text-muted-foreground mt-0.5">Limit: {Number(c.credit_limit).toLocaleString("en-IN")}</div>
                      )}
                    </TableCell>
                    <TableCell className="min-w-[150px]">
                      {c.payment_terms_basis
                        ? <Badge variant="outline" className="bg-muted/50 font-normal" data-testid={`customer-ref-${c.id}`}>{c.payment_terms_basis}</Badge>
                        : <span className="text-[11px] text-muted-foreground">—</span>}
                    </TableCell>
                    <TableCell className="text-sm">{c.invoice_count}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={bucketBadgeClass(c.bucket)}>{c.max_days_overdue}d</Badge>
                    </TableCell>
                    <TableCell className="text-right font-mono font-semibold">{c.total_display}</TableCell>
                    <TableCell className="text-center">
                      <Switch checked={c.excluded} onCheckedChange={(v) => onSwitch(c, v)}
                              data-testid={`customer-exclude-toggle-${c.id}`} />
                    </TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="icon" className="text-muted-foreground hover:text-rose-500"
                              onClick={() => setDeleteTarget(c)} data-testid={`customer-delete-${c.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>

      <Dialog open={!!excludeTarget} onOpenChange={(o) => !o && setExcludeTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldAlert className="w-5 h-5 text-rose-500" /> Exclude from reminders
            </DialogTitle>
            <DialogDescription>
              {excludeTarget?.customer_name} will be skipped in all automatic reminder runs. Add a reason for the record.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label>Reason (optional)</Label>
            <Textarea value={reason} onChange={(e) => setReason(e.target.value)}
                      placeholder="e.g. Under insolvency / written off / disputed"
                      data-testid="exclude-reason-input" />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setExcludeTarget(null)}>Cancel</Button>
            <Button onClick={confirmExclude} data-testid="exclude-confirm-button">
              <ShieldCheck className="w-4 h-4 mr-2" /> Exclude
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove {deleteTarget?.customer_name}?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently deletes the customer and all {deleteTarget?.invoice_count} associated invoices. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} data-testid="delete-confirm-button"
              className="bg-rose-600 hover:bg-rose-700">Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={!!masterTarget} onOpenChange={(o) => { if (!o) closeMaster(); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileCog className="w-5 h-5 text-primary" /> Customer Master — {masterTarget?.customer_name}
            </DialogTitle>
            <DialogDescription>
              To emails receive reminders automatically. Payment terms (days + basis) drive due-date verification.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label>To Emails (reminders sent here)</Label>
            <EmailsInput value={master.emails} onChange={(v) => setMaster((m) => ({ ...m, emails: v }))} testid="master-emails-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Payment Terms (days)</Label>
              <Input type="number" min="0" value={master.payment_terms_days}
                     onChange={(e) => setMaster((m) => ({ ...m, payment_terms_days: e.target.value }))}
                     data-testid="master-terms-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Terms Basis</Label>
              <Select value={master.payment_terms_basis}
                      onValueChange={(v) => setMaster((m) => ({ ...m, payment_terms_basis: v }))}>
                <SelectTrigger data-testid="master-basis-select"><SelectValue placeholder="Select basis" /></SelectTrigger>
                <SelectContent>
                  {(BASIS[masterTarget?.source] || BASIS.domestic).map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Credit Limit</Label>
              <Input type="number" min="0" value={master.credit_limit}
                     onChange={(e) => setMaster((m) => ({ ...m, credit_limit: e.target.value }))}
                     data-testid="master-credit-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Contact Person</Label>
              <Input value={master.contact_person}
                     onChange={(e) => setMaster((m) => ({ ...m, contact_person: e.target.value }))}
                     placeholder="Accounts contact" data-testid="master-contact-input" />
            </div>
          </div>
          <DialogFooter className="items-center sm:justify-between">
            <span className="text-xs text-muted-foreground">Changes are saved automatically.</span>
            <Button onClick={closeMaster} data-testid="master-save-button">Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
